export type Lang = "de" | "en" | "fr" | "es";

export type Copy = {
  meta: { title: string; description: string };
  nav: { why: string; compare: string; faq: string; cta: string };
  hero: {
    eyebrow: string;
    headline: string;
    headlineAccent: string;
    subtitle: string;
  };
  form: {
    badge: string;
    title: string;
    dob: string;
    dobPh: string;
    time: string;
    timePh: string;
    city: string;
    cityPh: string;
    country: string;
    cta: string;
    trust: string;
  };
  modal: {
    badge: string;
    title: string;
    lagna: string;
    nakshatra: string;
    rashi: string;
    lockedTitle: string;
    lockedBody: string;
    unlock: string;
    app: string;
    again: string;
  };
  compare: {
    eyebrow: string;
    title: string;
    paragraphs: string[];
    tropicalLabel: string;
    tropicalPoints: string[];
    siderealLabel: string;
    siderealPoints: string[];
  };
  why: {
    eyebrow: string;
    title: string;
    cards: { title: string; subtitle: string; body: string }[];
  };
  faq: {
    eyebrow: string;
    title: string;
    items: { q: string; a: string }[];
  };
  contact: {
    eyebrow: string;
    title: string;
    subtitle: string;
    name: string;
    namePh: string;
    email: string;
    emailPh: string;
    message: string;
    messagePh: string;
    cta: string;
    success: string;
  };
  app: {
    login: {
      signInEyebrow: string;
      signInTitle: string;
      signUpEyebrow: string;
      signUpTitle: string;
      subtitle: string;
      nameLabel: string;
      emailLabel: string;
      passwordLabel: string;
      submitLogin: string;
      submitRegister: string;
      submitting: string;
      registerSuccess: string;
      noAccount: string;
      switchToRegister: string;
      haveAccount: string;
      switchToLogin: string;
      backHome: string;
      forgotPassword: string;
      resetTitle: string;
      resetHint: string;
      resetSend: string;
      resetSending: string;
      resetSuccess: string;
      resetCancel: string;
      recoveryEyebrow: string;
      recoveryTitle: string;
      recoveryHint: string;
      recoveryNewPassword: string;
      recoveryConfirmPassword: string;
      recoverySave: string;
      recoverySaving: string;
      recoverySuccess: string;
      recoveryContinue: string;
      recoveryTooShort: string;
      recoveryMismatch: string;
      forceChangeEyebrow: string;
      forceChangeTitle: string;
      forceChangeHint: string;
    };
    admin: {
      titleAdmin: string;
      titleStaff: string;
      welcome: string;
      logout: string;
      footerNote: string;
      tabOverview: string;
      tabInbox: string;
      tabContent: string;
      tabTeam: string;
      tabTexts: string;
      loading: string;
      badgeDemo: string;
      badgeLive: string;
      infra: {
        title: string;
        status: string;
        statusOnline: string;
        statusOffline: string;
        statusChecking: string;
        latency: string;
        latencySub: string;
        checks: string;
        checksSub: string;
        lastChecked: string;
        lastCheckedNever: string;
        latencyChart: string;
        checksChart: string;
        sessionNote: string;
        sessionScope: string;
      };
      traffic: {
        title: string;
        realtime: string;
        realtimeSub: string;
        sessionsToday: string;
        engagement: string;
        avgDuration: string;
        sessionsChart: string;
        sourcesChart: string;
      };
      geo: {
        title: string;
        mapTitle: string;
        mapCaption: string;
        topCountries: string;
        languages: string;
        timezones: string;
      };
      users: {
        title: string;
        total: string;
        newToday: string;
        newWeekSuffix: string;
        customers: string;
        team: string;
        adminSuffix: string;
        churnNote: string;
      };
      business: {
        title: string;
        revenue: string;
        revenueSub: string;
        conversion: string;
        conversionSub: string;
        aov: string;
        bookingsTotal: string;
        ranking: string;
        rankingEmpty: string;
      };
      charts: {
        title: string;
        total: string;
        today: string;
        week: string;
        topRashi: string;
      };
      activity: {
        title: string;
        empty: string;
      };
      security: {
        title: string;
        openTickets: string;
        totalSuffix: string;
        securityEvents: string;
        securityEventsSub: string;
        log: string;
        logNote: string;
      };
      inbox: {
        empty: string;
        selectPrompt: string;
        markAnswered: string;
        markOpen: string;
        replyPlaceholder: string;
        replyButton: string;
      };
      content: {
        save: string;
        saving: string;
        savedAt: string;
      };
      team: {
        title: string;
        inviteTitle: string;
        inviteHint: string;
        emailLabel: string;
        roleLabel: string;
        roleStaff: string;
        roleAdmin: string;
        inviteButton: string;
        noStaff: string;
      };
      media: {
        tabLabel: string;
        title: string;
        hint: string;
        currentLabel: string;
        defaultBadge: string;
        customBadge: string;
        altLabel: string;
        uploadButton: string;
        dropHint: string;
        uploading: string;
        uploadedAt: string;
        error: string;
        resetButton: string;
        resetting: string;
        resetConfirm: string;
        filterAll: string;
        filterOffers: string;
        filterPages: string;
        filterGlobal: string;
      };
    };
  };
};

export const CONTENT: Record<Lang, Copy> = {
  de: {
    meta: {
      title: "Jyotish Astro — 86% lesen das falsche Sternzeichen. Welches ist deins?",
      description:
        "Sidereale vedische Astrologie mit mathematischer Präzision. Entdecke dein echtes Lagna, deine 27 Nakshatras und deine aktuelle Vimshottari-Dasha — kostenlos berechnet.",
    },
    nav: { why: "Die 3 Säulen", compare: "Tropisch vs. Sidereal", faq: "FAQ", cta: "Chart berechnen" },
    hero: {
      eyebrow: "Sidereal · Jyotish · Mathematisch Exakt",
      headline: "86% aller Menschen lesen das falsche Sternzeichen.",
      headlineAccent: "Welches ist deins wirklich?",
      subtitle:
        "Die westliche (tropische) Astrologie ist durch die Präzession der Erdachse um etwa 24° von den echten Sternbildern abgedriftet. Das sidereale Jyotish-System korrigiert diese Verschiebung und berechnet die realen Planetenpositionen am Himmel — dein wahres Geburtschart, mathematisch präzise.",
    },
    form: {
      badge: "Kostenloses Sidereales Chart",
      title: "Berechne deinen echten Seelenplan",
      dob: "Geburtsdatum (TT.MM.JJJJ)",
      dobPh: "15.08.1990",
      time: "Exakte Geburtszeit (HH:MM)",
      timePh: "14:30",
      city: "Geburtsort (Stadt)",
      cityPh: "München",
      country: "Geburtsland",
      cta: "Echten Seelenplan berechnen (Kostenlos)",
      trust: "100% kostenlos · Keine Kreditkarte · Sofortiges Ergebnis",
    },
    modal: {
      badge: "Dein Aha-Moment ✦",
      title: "Dein echtes sidereales Chart",
      lagna: "Lagna (Aszendent)",
      nakshatra: "Nakshatra (Mondhaus)",
      rashi: "Rashi (Mondzeichen)",
      lockedTitle: "Deine aktuelle Dasha ist berechnet",
      lockedBody:
        "Der vollständige Zeitplan deiner Lebensphasen — Karriere, Liebe, Wohlstand und spirituelles Wachstum — wartet im ausführlichen Premium-Report.",
      unlock: "VIP Cosmic Coaching entdecken (489€ / 3 Monate)",
      app: "App Coming Soon — zur Warteliste",
      again: "Neue Berechnung",
    },
    compare: {
      eyebrow: "Tropisch vs. Sidereal",
      title: "Warum dein westliches Sternzeichen wahrscheinlich falsch ist.",
      paragraphs: [
        "Die Erdachse taumelt wie ein Kreisel langsam durch den Raum — ein Phänomen, das als Präzession der Tagundnachtgleichen bekannt ist. Ein vollständiger Zyklus dauert rund 25.800 Jahre. Vor etwa 2.000 Jahren, als die westliche Astrologie ihre Zeichen festlegte, standen Frühlingspunkt und Sternbild Widder noch nahezu deckungsgleich. Seither haben sie sich um fast 24° voneinander entfernt.",
        "Die tropische (westliche) Astrologie ignoriert diese Verschiebung bewusst: Sie bindet die Zeichen starr an die Jahreszeiten und den Sonnenstand zur Tagundnachtgleiche — nicht an die tatsächlichen Sterne. Das Ergebnis: Wer sich für einen Widder hält, hat die Sonne astronomisch betrachtet oft in den Fischen. Fast neun von zehn Menschen leben so unter einem Zeichen, das mit dem realen Himmel nicht übereinstimmt.",
        "Die sidereale vedische Astrologie (Jyotish) rechnet dagegen mit dem echten, beobachtbaren Sternenhimmel. Über den Lahiri-Ayanamsa — einen präzisen Korrekturwert — werden die Planetenpositionen auf die realen Sternbilder zurückgeführt. Dadurch entsteht ein Chart, das mit der Astronomie übereinstimmt und die Grundlage für aussagekräftige Aussagen über Charakter, Timing und Lebensphasen bildet.",
      ],
      tropicalLabel: "Tropisch (Westlich)",
      tropicalPoints: [
        "An die Jahreszeiten gebunden, nicht an die Sterne",
        "Ignoriert die 24°-Präzessionsverschiebung",
        "12 breite Sonnenzeichen",
        "Kein präzises Timing-System",
      ],
      siderealLabel: "Sidereal (Jyotish)",
      siderealPoints: [
        "An den echten Sternenhimmel gebunden",
        "Lahiri-Ayanamsa korrigiert die Präzession",
        "27 Nakshatras + 12 Rashis für Tiefe",
        "Vimshottari-Dasha für exaktes Timing",
      ],
    },
    why: {
      eyebrow: "Die 3 Säulen des Jyotish",
      title: "Drei Koordinaten. Ein vollständiger Seelenplan.",
      cards: [
        {
          title: "Lagna",
          subtitle: "Der wahre Aszendent",
          body: "Das Lagna ist der Grad des Tierkreises, der im Moment deiner Geburt am Osthorizont aufstieg. Es wechselt etwa alle zwei Stunden und bestimmt die gesamte Häuserstruktur deines Charts — dein Auftreten, deinen Körper und den Weg, den deine Seele in diesem Leben eingeschlagen hat. Ohne exakte Geburtszeit kein exaktes Lagna.",
        },
        {
          title: "Nakshatras",
          subtitle: "27 Mondhäuser",
          body: "Wo die westliche Astrologie nur 12 grobe Zeichen kennt, teilt Jyotish den Himmel in 27 Nakshatras — feine Mondmansionen mit je eigener Gottheit, Symbolik und Psychologie. Dein Geburts-Nakshatra beschreibt deine tiefste Natur, deine Instinkte und deine emotionalen Muster mit einer Präzision, die 12 Zeichen niemals erreichen.",
        },
        {
          title: "Vimshottari Dasha",
          subtitle: "Der Zeitplan deines Lebens",
          body: "Die Dasha ist das Herzstück des Jyotish: ein 120-jähriger Zyklus planetarer Perioden, der zeigt, wann welche Kräfte in deinem Leben aktiv werden. Sie beantwortet nicht nur das Was, sondern das Wann — wann Karriere, Liebe und Wohlstand reifen. Timing, das keine westliche Methode bietet.",
        },
      ],
    },
    faq: {
      eyebrow: "Häufige Fragen",
      title: "Was interessierte Suchende fragen.",
      items: [
        { q: "Warum ist mein Sternzeichen hier anders als gewohnt?", a: "Weil Jyotish das sidereale System nutzt, das mit den echten Sternpositionen rechnet. Die westliche Astrologie ist durch die Präzession um etwa 24° verschoben, wodurch die meisten Menschen im tropischen System ein Zeichen zu hoch eingeordnet werden. Dein siderales Zeichen ist astronomisch korrekt." },
        { q: "Brauche ich wirklich meine exakte Geburtszeit?", a: "Für das Lagna (Aszendent) und die Häuseraufteilung: ja. Das Lagna wechselt etwa alle zwei Stunden, daher verändert schon eine Abweichung von wenigen Minuten die Häuser. Rashi (Mondzeichen) und dein Nakshatra sind auch mit ungefährer Zeit aussagekräftig, aber für ein vollständiges Chart empfehlen wir die genaue Uhrzeit von deiner Geburtsurkunde." },
        { q: "Welches Häusersystem verwendet ihr?", a: "Wir nutzen das klassische, in Nordindien verbreitete System der ganzen Zeichen (Whole Sign Houses), bei dem jedes Rashi ein vollständiges Haus bildet. Es ist das ursprüngliche vedische System und liefert klare, unverzerrte Häuserzuordnungen — besonders in Kombination mit den Dasha-Perioden." },
        { q: "Wie lese ich mein Ergebnis richtig?", a: "Beginne mit deinem Lagna — es ist die Bühne deines Lebens. Dann betrachte dein Rashi und Nakshatra für deine emotionale und psychologische Natur. Zuletzt zeigt dir deine laufende Vimshottari-Dasha, welches Kapitel du gerade durchlebst. Der Premium-Report führt dich Schritt für Schritt durch diese Ebenen." },
        { q: "Wie unterscheidet sich das von westlicher Astrologie?", a: "Die westliche Astrologie ist tropisch (an Jahreszeiten gebunden), arbeitet mit 12 Zeichen und ohne systematisches Timing. Jyotish ist sidereal (an echten Sternen), nutzt 27 Nakshatras für Tiefe und die Vimshottari-Dasha für präzises Timing. Das Ergebnis ist deutlich persönlicher, konkreter und astronomisch fundiert." },
      ],
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Fragen an unsere vedischen Astrologen?",
      subtitle: "Schreib uns — wir antworten persönlich und diskret, meist innerhalb von 24 Stunden.",
      name: "Name",
      namePh: "Dein Name",
      email: "E-Mail",
      emailPh: "name@beispiel.de",
      message: "Nachricht / Anfrage",
      messagePh: "Deine Frage zu deinem Chart, einer Dasha oder dem Premium-Report …",
      cta: "Nachricht senden",
      success: "Danke! Deine Nachricht ist eingegangen — wir melden uns bald.",
    },
    app: {
      login: {
        signInEyebrow: "Willkommen zurück",
        signInTitle: "Anmelden",
        signUpEyebrow: "Konto erstellen",
        signUpTitle: "Registrieren",
        subtitle: "Zugang zu deinem Kundenportal oder dem Team-Bereich.",
        nameLabel: "Name",
        emailLabel: "E-Mail",
        passwordLabel: "Passwort",
        submitLogin: "Anmelden",
        submitRegister: "Registrieren",
        submitting: "Einen Moment …",
        registerSuccess: "Konto erstellt! Falls E-Mail-Bestätigung aktiviert ist, prüfe dein Postfach — sonst bist du gleich eingeloggt.",
        noAccount: "Noch kein Konto?",
        switchToRegister: "Registrieren",
        haveAccount: "Schon ein Konto?",
        switchToLogin: "Anmelden",
        backHome: "← Zurück zur Startseite",
        forgotPassword: "Passwort vergessen?",
        resetTitle: "Passwort zurücksetzen",
        resetHint: "Gib deine E-Mail-Adresse ein — wir senden dir einen Link zum Zurücksetzen.",
        resetSend: "Link senden",
        resetSending: "Sende …",
        resetSuccess: "Falls ein Konto mit dieser E-Mail existiert, wurde ein Reset-Link gesendet. Bitte prüfe dein Postfach.",
        resetCancel: "Abbrechen",
        recoveryEyebrow: "Passwort-Wiederherstellung",
        recoveryTitle: "Neues Passwort festlegen",
        recoveryHint: "Du bist über deinen Reset-Link zurückgekehrt. Lege jetzt dein neues Passwort fest.",
        recoveryNewPassword: "Neues Passwort",
        recoveryConfirmPassword: "Passwort bestätigen",
        recoverySave: "Passwort speichern",
        recoverySaving: "Speichert …",
        recoverySuccess: "Dein Passwort wurde erfolgreich geändert.",
        recoveryContinue: "Weiter",
        recoveryTooShort: "Das Passwort muss mindestens 6 Zeichen lang sein.",
        recoveryMismatch: "Die Passwörter stimmen nicht überein.",
        forceChangeEyebrow: "Sicherheit",
        forceChangeTitle: "Passwort jetzt ändern",
        forceChangeHint: "Dieses Konto wurde mit einem temporären Passwort angelegt. Bitte lege jetzt dein eigenes, persönliches Passwort fest, bevor du fortfährst.",
      },
      admin: {
        titleAdmin: "Admin",
        titleStaff: "Team",
        welcome: "Willkommen",
        logout: "Abmelden",
        footerNote: "Backend: Supabase (Postgres + RLS).",
        tabOverview: "Übersicht",
        tabInbox: "Ticket-Inbox",
        tabContent: "Content-Manager",
        tabTeam: "Team-Verwaltung",
        tabTexts: "Website-Texte",
        loading: "Lädt …",
        badgeDemo: "Demo-Daten",
        badgeLive: "Live · Supabase",
        infra: {
          title: "Technische Performance & System",
          status: "Datenbank-Status",
          statusOnline: "Online",
          statusOffline: "Nicht erreichbar",
          statusChecking: "Prüft …",
          latency: "Datenbank-Latenz",
          latencySub: "Letzte Messung",
          checks: "Checks (diese Sitzung)",
          checksSub: "erfolgreich / fehlgeschlagen",
          lastChecked: "Zuletzt geprüft",
          lastCheckedNever: "Noch nicht geprüft",
          latencyChart: "Latenz-Verlauf (diese Sitzung)",
          checksChart: "Checks nach Ergebnis (diese Sitzung)",
          sessionNote: "Live gemessen ab einer echten Supabase-Abfrage (profiles, HEAD/count) — kein Server-seitiges Monitoring, daher nur für diese Browser-Sitzung.",
          sessionScope: "Sitzungs-Monitoring",
        },
        traffic: {
          title: "Traffic & Nutzer-Engagement",
          realtime: "Echtzeit-Nutzer",
          realtimeSub: "Gerade aktiv",
          sessionsToday: "Sitzungen heute",
          engagement: "Engagement Rate",
          avgDuration: "Ø Sitzungsdauer",
          sessionsChart: "Sitzungen (7 Tage)",
          sourcesChart: "Traffic-Quellen",
        },
        geo: {
          title: "Geografie & Demografie",
          mapTitle: "Traffic-Hotspots (Weltkarte)",
          mapCaption: "Kreisgröße ≈ relativer Traffic-Anteil je Stadt (Demo-Daten)",
          topCountries: "Top-Länder",
          languages: "Sprachverteilung",
          timezones: "Zeitzonen-Hotspots",
        },
        users: {
          title: "Nutzerverwaltung & Community",
          total: "Registrierte Nutzer",
          newToday: "Neu heute",
          newWeekSuffix: "diese Woche",
          customers: "Kunden",
          team: "Team (Mitarbeiter/Admin)",
          adminSuffix: "Admin(s)",
          churnNote: "Churn-Rate / inaktive Accounts erfordern Login-Zeitstempel, die aktuell nicht erfasst werden — als nächster Schritt ergänzbar.",
        },
        business: {
          title: "Business & Conversions",
          revenue: "Umsatz (angefragt)",
          revenueSub: "Buchungsanfragen",
          conversion: "Conversion-Rate",
          conversionSub: "Anfragen / Unique Visitors (Monat, Demo-Traffic)",
          aov: "Ø Bestellwert (AOV)",
          bookingsTotal: "Buchungsanfragen gesamt",
          ranking: "Bestseller-Ranking",
          rankingEmpty: "Noch keine Angebots-Buchungsanfragen.",
        },
        charts: {
          title: "Horoskop-Rechner",
          total: "Charts insgesamt",
          today: "Heute berechnet",
          week: "Diese Woche",
          topRashi: "Häufigstes Rashi",
        },
        activity: {
          title: "Neueste Aktivitäten",
          empty: "Noch keine Aktivitäten.",
        },
        security: {
          title: "Administration & Sicherheit",
          openTickets: "Offene Support-Tickets",
          totalSuffix: "gesamt",
          securityEvents: "Security-Ereignisse (7 Tage)",
          securityEventsSub: "Demo-Daten",
          log: "Security-Log",
          logNote: "Echte fehlgeschlagene Logins erfordern die Supabase Auth Admin-API — muss serverseitig laufen, nie im Browser.",
        },
        inbox: {
          empty: "Noch keine Anfragen.",
          selectPrompt: "Wähle links eine Konversation aus.",
          markAnswered: "Als beantwortet markieren",
          markOpen: "Als offen markieren",
          replyPlaceholder: "Antwort schreiben …",
          replyButton: "Antworten",
        },
        content: {
          save: "Speichern",
          saving: "Speichert …",
          savedAt: "Zuletzt gespeichert um",
        },
        team: {
          title: "Team & Admins",
          inviteTitle: "Mitarbeiter einladen",
          inviteHint: "Trägt die E-Mail für eine Rolle vor. Die Person registriert sich selbst unter /login mit genau dieser E-Mail und erhält dann automatisch diese Rolle.",
          emailLabel: "E-Mail",
          roleLabel: "Rolle",
          roleStaff: "Mitarbeiter",
          roleAdmin: "Admin",
          inviteButton: "Einladen",
          noStaff: "Noch kein Team-Mitglied registriert.",
        },
        media: {
          tabLabel: "Medien & Seitenbilder",
          title: "Medien & Seitenbilder",
          hint: "Ersetze die Platzhalterbilder auf den öffentlichen Seiten. Änderungen erscheinen live, sobald hochgeladen.",
          currentLabel: "Aktuelles Bild",
          defaultBadge: "Platzhalter",
          customBadge: "Eigenes Bild",
          altLabel: "Alt-Text (Barrierefreiheit)",
          uploadButton: "Bild hochladen",
          uploading: "Lädt hoch …",
          uploadedAt: "Zuletzt geändert",
          error: "Upload fehlgeschlagen.",
          resetButton: "Zurücksetzen",
          resetting: "Setzt zurück …",
          resetConfirm: "Bild wirklich auf den Standard-Platzhalter zurücksetzen?",
          filterAll: "Alle",
          filterOffers: "Angebote & Pakete",
          filterPages: "Seiten",
          filterGlobal: "Global",
          dropHint: "Datei hierher ziehen oder klicken",
        },
      },
    },
  },

  en: {
    meta: {
      title: "Jyotish Astro — 86% follow the wrong zodiac sign. What is yours?",
      description:
        "Sidereal Vedic astrology with mathematical precision. Discover your true Lagna, your 27 Nakshatras and your current Vimshottari Dasha — calculated free.",
    },
    nav: { why: "The 3 Pillars", compare: "Tropical vs. Sidereal", faq: "FAQ", cta: "Calculate chart" },
    hero: {
      eyebrow: "Sidereal · Jyotish · Mathematically Exact",
      headline: "86% of people follow the wrong zodiac sign.",
      headlineAccent: "What is yours really?",
      subtitle:
        "Western (tropical) astrology has drifted roughly 24° from the real constellations due to the precession of Earth's axis. The sidereal Jyotish system corrects this shift and computes the actual planetary positions in the sky — your true birth chart, with mathematical precision.",
    },
    form: {
      badge: "Free Sidereal Chart",
      title: "Calculate your true blueprint",
      dob: "Date of Birth (DD.MM.YYYY)",
      dobPh: "15.08.1990",
      time: "Exact Birth Time (HH:MM)",
      timePh: "14:30",
      city: "Birthplace (City)",
      cityPh: "Munich",
      country: "Country of birth",
      cta: "Calculate True Blueprint (Free)",
      trust: "100% free · No credit card · Instant result",
    },
    modal: {
      badge: "Your Aha Moment ✦",
      title: "Your true sidereal chart",
      lagna: "Lagna (Ascendant)",
      nakshatra: "Nakshatra (Lunar Mansion)",
      rashi: "Rashi (Moon Sign)",
      lockedTitle: "Your current Dasha is calculated",
      lockedBody:
        "The full timeline of your life phases — career, love, wealth and spiritual growth — awaits in the detailed Premium Report.",
      unlock: "Explore VIP Cosmic Coaching (€489 / 3 months)",
      app: "App Coming Soon — join the waitlist",
      again: "New calculation",
    },
    compare: {
      eyebrow: "Tropical vs. Sidereal",
      title: "Why your Western zodiac sign is probably wrong.",
      paragraphs: [
        "The Earth's axis slowly wobbles through space like a spinning top — a phenomenon called the precession of the equinoxes. One full cycle takes about 25,800 years. Roughly 2,000 years ago, when Western astrology fixed its signs, the spring equinox point and the constellation Aries lined up almost exactly. Since then they have drifted nearly 24° apart.",
        "Tropical (Western) astrology deliberately ignores this shift: it locks the signs to the seasons and the position of the Sun at the equinox — not to the actual stars. The result is stark: someone who believes they are an Aries astronomically often has the Sun in Pisces. Nearly nine out of ten people live under a sign that no longer matches the real sky.",
        "Sidereal Vedic astrology (Jyotish) instead calculates against the real, observable star field. Using the Lahiri Ayanamsa — a precise correction value — planetary positions are mapped back onto the true constellations. This produces a chart that agrees with astronomy and forms a reliable basis for reading character, timing and life phases.",
      ],
      tropicalLabel: "Tropical (Western)",
      tropicalPoints: [
        "Tied to the seasons, not the stars",
        "Ignores the 24° precession shift",
        "12 broad sun signs",
        "No precise timing system",
      ],
      siderealLabel: "Sidereal (Jyotish)",
      siderealPoints: [
        "Tied to the real star field",
        "Lahiri Ayanamsa corrects precession",
        "27 Nakshatras + 12 Rashis for depth",
        "Vimshottari Dasha for exact timing",
      ],
    },
    why: {
      eyebrow: "The 3 Pillars of Jyotish",
      title: "Three coordinates. One complete blueprint.",
      cards: [
        {
          title: "Lagna",
          subtitle: "The True Rising Sign",
          body: "The Lagna is the exact degree of the zodiac rising on the eastern horizon at the moment of your birth. It changes roughly every two hours and sets the entire house structure of your chart — your appearance, your body, and the path your soul has taken in this life. No exact birth time means no exact Lagna.",
        },
        {
          title: "Nakshatras",
          subtitle: "27 Lunar Mansions",
          body: "Where Western astrology knows only 12 broad signs, Jyotish divides the sky into 27 Nakshatras — fine lunar mansions, each with its own deity, symbolism and psychology. Your birth Nakshatra describes your deepest nature, instincts and emotional patterns with a precision that 12 signs can never reach.",
        },
        {
          title: "Vimshottari Dasha",
          subtitle: "Your Life Timeline",
          body: "The Dasha is the heart of Jyotish: a 120-year cycle of planetary periods that reveals when specific forces become active in your life. It answers not only the what, but the when — when career, love and wealth ripen. Timing that no Western method offers.",
        },
      ],
    },
    faq: {
      eyebrow: "Common Questions",
      title: "What curious seekers ask.",
      items: [
        { q: "Why is my sign different here than I'm used to?", a: "Because Jyotish uses the sidereal system, which calculates against the real star positions. Western astrology is shifted about 24° due to precession, so most people are placed one sign too high in the tropical system. Your sidereal sign is astronomically correct." },
        { q: "Do I really need my exact birth time?", a: "For the Lagna (ascendant) and house layout: yes. The Lagna changes roughly every two hours, so even a few minutes' difference alters the houses. Rashi (moon sign) and your Nakshatra are meaningful with an approximate time, but for a complete chart we recommend the exact time from your birth certificate." },
        { q: "Which house system do you use?", a: "We use the classical Whole Sign house system common in North India, where each Rashi forms one complete house. It is the original Vedic system and produces clear, undistorted house assignments — especially powerful in combination with the Dasha periods." },
        { q: "How do I read my result correctly?", a: "Start with your Lagna — it is the stage of your life. Then look at your Rashi and Nakshatra for your emotional and psychological nature. Finally, your running Vimshottari Dasha shows which chapter you are living through now. The Premium Report walks you through these layers step by step." },
        { q: "How does this differ from Western astrology?", a: "Western astrology is tropical (tied to seasons), uses 12 signs and has no systematic timing. Jyotish is sidereal (tied to real stars), uses 27 Nakshatras for depth and the Vimshottari Dasha for precise timing. The result is far more personal, concrete and astronomically grounded." },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Questions for our Vedic astrologers?",
      subtitle: "Write to us — we reply personally and discreetly, usually within 24 hours.",
      name: "Name",
      namePh: "Your name",
      email: "Email",
      emailPh: "name@example.com",
      message: "Message / Inquiry",
      messagePh: "Your question about your chart, a Dasha or the Premium Report …",
      cta: "Send message",
      success: "Thank you! Your message has been received — we'll be in touch soon.",
    },
    app: {
      login: {
        signInEyebrow: "Welcome back",
        signInTitle: "Sign in",
        signUpEyebrow: "Create an account",
        signUpTitle: "Register",
        subtitle: "Access your customer portal or the team area.",
        nameLabel: "Name",
        emailLabel: "Email",
        passwordLabel: "Password",
        submitLogin: "Sign in",
        submitRegister: "Register",
        submitting: "One moment …",
        registerSuccess: "Account created! If email confirmation is enabled, check your inbox — otherwise you're signed in already.",
        noAccount: "No account yet?",
        switchToRegister: "Register",
        haveAccount: "Already have an account?",
        switchToLogin: "Sign in",
        backHome: "← Back to homepage",
        forgotPassword: "Forgot your password?",
        resetTitle: "Reset password",
        resetHint: "Enter your email address — we'll send you a link to reset it.",
        resetSend: "Send link",
        resetSending: "Sending …",
        resetSuccess: "If an account with this email exists, a reset link has been sent. Please check your inbox.",
        resetCancel: "Cancel",
        recoveryEyebrow: "Password Recovery",
        recoveryTitle: "Set a new password",
        recoveryHint: "You've returned via your reset link. Set your new password now.",
        recoveryNewPassword: "New password",
        recoveryConfirmPassword: "Confirm password",
        recoverySave: "Save password",
        recoverySaving: "Saving …",
        recoverySuccess: "Your password has been changed successfully.",
        recoveryContinue: "Continue",
        recoveryTooShort: "Password must be at least 6 characters.",
        recoveryMismatch: "Passwords do not match.",
        forceChangeEyebrow: "Security",
        forceChangeTitle: "Change your password now",
        forceChangeHint: "This account was created with a temporary password. Please set your own personal password now before continuing.",
      },
      admin: {
        titleAdmin: "Admin",
        titleStaff: "Staff",
        welcome: "Welcome",
        logout: "Log out",
        footerNote: "Backend: Supabase (Postgres + RLS).",
        tabOverview: "Overview",
        tabInbox: "Ticket Inbox",
        tabContent: "Content Manager",
        tabTeam: "Team Management",
        tabTexts: "Website Texts",
        loading: "Loading …",
        badgeDemo: "Demo Data",
        badgeLive: "Live · Supabase",
        infra: {
          title: "Technical Performance & System",
          status: "Database Status",
          statusOnline: "Online",
          statusOffline: "Unreachable",
          statusChecking: "Checking …",
          latency: "Database Latency",
          latencySub: "Latest measurement",
          checks: "Checks (this session)",
          checksSub: "successful / failed",
          lastChecked: "Last checked",
          lastCheckedNever: "Not checked yet",
          latencyChart: "Latency history (this session)",
          checksChart: "Checks by outcome (this session)",
          sessionNote: "Live-measured from a real Supabase query (profiles, HEAD/count) — no server-side monitoring, so this only covers the current browser session.",
          sessionScope: "Session Monitoring",
        },
        traffic: {
          title: "Traffic & User Engagement",
          realtime: "Realtime Users",
          realtimeSub: "Active right now",
          sessionsToday: "Sessions today",
          engagement: "Engagement Rate",
          avgDuration: "Avg. Session Duration",
          sessionsChart: "Sessions (7 days)",
          sourcesChart: "Traffic Sources",
        },
        geo: {
          title: "Geography & Demographics",
          mapTitle: "Traffic Hotspots (World Map)",
          mapCaption: "Circle size ≈ relative traffic share per city (demo data)",
          topCountries: "Top Countries",
          languages: "Language Distribution",
          timezones: "Timezone Hotspots",
        },
        users: {
          title: "User Management & Community",
          total: "Registered Users",
          newToday: "New today",
          newWeekSuffix: "this week",
          customers: "Customers",
          team: "Team (Staff/Admin)",
          adminSuffix: "Admin(s)",
          churnNote: "Churn rate / inactive accounts require login timestamps, not currently tracked — addable as a next step.",
        },
        business: {
          title: "Business & Conversions",
          revenue: "Revenue (requested)",
          revenueSub: "booking inquiries",
          conversion: "Conversion Rate",
          conversionSub: "Inquiries / unique visitors (month, demo traffic)",
          aov: "Avg. Order Value (AOV)",
          bookingsTotal: "Total booking inquiries",
          ranking: "Bestseller Ranking",
          rankingEmpty: "No offer booking inquiries yet.",
        },
        charts: {
          title: "Horoscope Calculator",
          total: "Charts total",
          today: "Calculated today",
          week: "This week",
          topRashi: "Most common Rashi",
        },
        activity: {
          title: "Recent Activity",
          empty: "No activity yet.",
        },
        security: {
          title: "Administration & Security",
          openTickets: "Open support tickets",
          totalSuffix: "total",
          securityEvents: "Security events (7 days)",
          securityEventsSub: "Demo data",
          log: "Security log",
          logNote: "Real failed logins require the Supabase Auth admin API — must run server-side, never in the browser.",
        },
        inbox: {
          empty: "No inquiries yet.",
          selectPrompt: "Select a conversation on the left.",
          markAnswered: "Mark as answered",
          markOpen: "Mark as open",
          replyPlaceholder: "Write a reply …",
          replyButton: "Reply",
        },
        content: {
          save: "Save",
          saving: "Saving …",
          savedAt: "Last saved at",
        },
        team: {
          title: "Team & Admins",
          inviteTitle: "Invite staff member",
          inviteHint: "Pre-authorizes an email for a role. That person signs up at /login with the exact same email and automatically receives this role.",
          emailLabel: "Email",
          roleLabel: "Role",
          roleStaff: "Staff",
          roleAdmin: "Admin",
          inviteButton: "Invite",
          noStaff: "No team member registered yet.",
        },
        media: {
          tabLabel: "Media & Page Images",
          title: "Media & Page Images",
          hint: "Replace placeholder images on public pages. Changes appear live as soon as they're uploaded.",
          currentLabel: "Current image",
          defaultBadge: "Placeholder",
          customBadge: "Custom image",
          altLabel: "Alt text (accessibility)",
          uploadButton: "Upload image",
          uploading: "Uploading …",
          uploadedAt: "Last changed",
          error: "Upload failed.",
          resetButton: "Reset",
          resetting: "Resetting …",
          resetConfirm: "Really reset this image to the default placeholder?",
          filterAll: "All",
          filterOffers: "Offers & Packages",
          filterPages: "Pages",
          filterGlobal: "Global",
          dropHint: "Drag a file here or click to browse",
        },
      },
    },
  },

  fr: {
    meta: {
      title: "Jyotish Astro — 86% lisent le mauvais signe. Quel est le vôtre ?",
      description:
        "Astrologie védique sidérale d'une précision mathématique. Découvrez votre vrai Lagna, vos 27 Nakshatras et votre Dasha Vimshottari actuel — calculé gratuitement.",
    },
    nav: { why: "Les 3 Piliers", compare: "Tropical vs. Sidéral", faq: "FAQ", cta: "Calculer" },
    hero: {
      eyebrow: "Sidéral · Jyotish · Mathématiquement Exact",
      headline: "86% des gens lisent le mauvais signe du zodiaque.",
      headlineAccent: "Quel est vraiment le vôtre ?",
      subtitle:
        "L'astrologie occidentale (tropicale) a dérivé d'environ 24° par rapport aux vraies constellations, à cause de la précession de l'axe terrestre. Le système sidéral Jyotish corrige ce décalage et calcule les positions planétaires réelles dans le ciel — votre vrai thème natal, avec une précision mathématique.",
    },
    form: {
      badge: "Thème Sidéral Gratuit",
      title: "Calculez votre véritable plan d'âme",
      dob: "Date de naissance (JJ.MM.AAAA)",
      dobPh: "15.08.1990",
      time: "Heure exacte (HH:MM)",
      timePh: "14:30",
      city: "Lieu de naissance (Ville)",
      cityPh: "Paris",
      country: "Pays de naissance",
      cta: "Calculer le Vrai Plan (Gratuit)",
      trust: "100% gratuit · Sans carte · Résultat instantané",
    },
    modal: {
      badge: "Votre moment Aha ✦",
      title: "Votre vrai thème sidéral",
      lagna: "Lagna (Ascendant)",
      nakshatra: "Nakshatra (Maison lunaire)",
      rashi: "Rashi (Signe lunaire)",
      lockedTitle: "Votre Dasha actuel est calculé",
      lockedBody:
        "La chronologie complète de vos phases de vie — carrière, amour, richesse et croissance spirituelle — vous attend dans le rapport Premium détaillé.",
      unlock: "Découvrir VIP Cosmic Coaching (489€ / 3 mois)",
      app: "App Coming Soon — rejoindre la liste d'attente",
      again: "Nouveau calcul",
    },
    compare: {
      eyebrow: "Tropical vs. Sidéral",
      title: "Pourquoi votre signe occidental est probablement faux.",
      paragraphs: [
        "L'axe de la Terre oscille lentement dans l'espace comme une toupie — un phénomène appelé la précession des équinoxes. Un cycle complet dure environ 25 800 ans. Il y a environ 2 000 ans, lorsque l'astrologie occidentale a fixé ses signes, le point vernal et la constellation du Bélier coïncidaient presque exactement. Depuis, ils se sont éloignés de près de 24°.",
        "L'astrologie tropicale (occidentale) ignore délibérément ce décalage : elle fixe les signes aux saisons et à la position du Soleil à l'équinoxe — et non aux véritables étoiles. Le résultat est frappant : une personne qui se croit Bélier a souvent, astronomiquement, le Soleil dans les Poissons. Près de neuf personnes sur dix vivent sous un signe qui ne correspond plus au ciel réel.",
        "L'astrologie védique sidérale (Jyotish) calcule au contraire d'après le champ stellaire réel et observable. Grâce à l'Ayanamsa de Lahiri — une valeur de correction précise — les positions planétaires sont ramenées sur les vraies constellations. On obtient un thème conforme à l'astronomie, base fiable pour lire le caractère, le timing et les phases de vie.",
      ],
      tropicalLabel: "Tropical (Occidental)",
      tropicalPoints: [
        "Lié aux saisons, pas aux étoiles",
        "Ignore le décalage de précession de 24°",
        "12 signes solaires larges",
        "Aucun système de timing précis",
      ],
      siderealLabel: "Sidéral (Jyotish)",
      siderealPoints: [
        "Lié au champ stellaire réel",
        "L'Ayanamsa de Lahiri corrige la précession",
        "27 Nakshatras + 12 Rashis pour la profondeur",
        "Dasha Vimshottari pour un timing exact",
      ],
    },
    why: {
      eyebrow: "Les 3 Piliers du Jyotish",
      title: "Trois coordonnées. Un plan d'âme complet.",
      cards: [
        {
          title: "Lagna",
          subtitle: "Le vrai signe ascendant",
          body: "Le Lagna est le degré exact du zodiaque qui se levait à l'horizon est au moment de votre naissance. Il change environ toutes les deux heures et définit toute la structure des maisons de votre thème — votre apparence, votre corps et le chemin que votre âme a choisi dans cette vie. Sans heure de naissance exacte, pas de Lagna exact.",
        },
        {
          title: "Nakshatras",
          subtitle: "27 maisons lunaires",
          body: "Là où l'astrologie occidentale ne connaît que 12 signes larges, le Jyotish divise le ciel en 27 Nakshatras — de fines maisons lunaires, chacune avec sa divinité, sa symbolique et sa psychologie. Votre Nakshatra de naissance décrit votre nature la plus profonde, vos instincts et vos schémas émotionnels avec une précision que 12 signes n'atteindront jamais.",
        },
        {
          title: "Dasha Vimshottari",
          subtitle: "La chronologie de votre vie",
          body: "Le Dasha est le cœur du Jyotish : un cycle de 120 ans de périodes planétaires qui révèle quand certaines forces s'activent dans votre vie. Il répond non seulement au quoi, mais au quand — quand mûrissent la carrière, l'amour et la richesse. Un timing qu'aucune méthode occidentale n'offre.",
        },
      ],
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "Ce que demandent les curieux.",
      items: [
        { q: "Pourquoi mon signe est-il différent d'habitude ?", a: "Parce que le Jyotish utilise le système sidéral, qui calcule d'après les vraies positions stellaires. L'astrologie occidentale est décalée d'environ 24° à cause de la précession, plaçant la plupart des gens un signe trop haut dans le système tropical. Votre signe sidéral est astronomiquement correct." },
        { q: "Ai-je vraiment besoin de mon heure exacte de naissance ?", a: "Pour le Lagna (ascendant) et la disposition des maisons : oui. Le Lagna change environ toutes les deux heures, donc même quelques minutes d'écart modifient les maisons. Le Rashi (signe lunaire) et votre Nakshatra restent parlants avec une heure approximative, mais pour un thème complet nous recommandons l'heure exacte de votre acte de naissance." },
        { q: "Quel système de maisons utilisez-vous ?", a: "Nous utilisons le système classique des signes entiers (Whole Sign) répandu en Inde du Nord, où chaque Rashi forme une maison complète. C'est le système védique d'origine, qui produit des attributions de maisons claires et non déformées — particulièrement puissant combiné aux périodes Dasha." },
        { q: "Comment lire correctement mon résultat ?", a: "Commencez par votre Lagna — c'est la scène de votre vie. Puis regardez votre Rashi et votre Nakshatra pour votre nature émotionnelle et psychologique. Enfin, votre Dasha Vimshottari en cours montre quel chapitre vous traversez actuellement. Le rapport Premium vous guide étape par étape à travers ces niveaux." },
        { q: "En quoi est-ce différent de l'astrologie occidentale ?", a: "L'astrologie occidentale est tropicale (liée aux saisons), utilise 12 signes et n'a aucun timing systématique. Le Jyotish est sidéral (lié aux vraies étoiles), utilise 27 Nakshatras pour la profondeur et le Dasha Vimshottari pour un timing précis. Le résultat est bien plus personnel, concret et fondé sur l'astronomie." },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Des questions pour nos astrologues védiques ?",
      subtitle: "Écrivez-nous — nous répondons personnellement et en toute discrétion, généralement sous 24 heures.",
      name: "Nom",
      namePh: "Votre nom",
      email: "E-mail",
      emailPh: "nom@exemple.fr",
      message: "Message / Demande",
      messagePh: "Votre question sur votre thème, un Dasha ou le rapport Premium …",
      cta: "Envoyer le message",
      success: "Merci ! Votre message a bien été reçu — nous vous répondrons bientôt.",
    },
    app: {
      login: {
        signInEyebrow: "Bon retour",
        signInTitle: "Connexion",
        signUpEyebrow: "Créer un compte",
        signUpTitle: "S'inscrire",
        subtitle: "Accédez à votre espace client ou à l'espace équipe.",
        nameLabel: "Nom",
        emailLabel: "E-mail",
        passwordLabel: "Mot de passe",
        submitLogin: "Connexion",
        submitRegister: "S'inscrire",
        submitting: "Un instant …",
        registerSuccess: "Compte créé ! Si la confirmation par e-mail est activée, vérifiez votre boîte de réception — sinon vous êtes déjà connecté(e).",
        noAccount: "Pas encore de compte ?",
        switchToRegister: "S'inscrire",
        haveAccount: "Déjà un compte ?",
        switchToLogin: "Connexion",
        backHome: "← Retour à l'accueil",
        forgotPassword: "Mot de passe oublié ?",
        resetTitle: "Réinitialiser le mot de passe",
        resetHint: "Entrez votre adresse e-mail — nous vous enverrons un lien de réinitialisation.",
        resetSend: "Envoyer le lien",
        resetSending: "Envoi …",
        resetSuccess: "Si un compte existe avec cet e-mail, un lien de réinitialisation a été envoyé. Vérifiez votre boîte de réception.",
        resetCancel: "Annuler",
        recoveryEyebrow: "Récupération du mot de passe",
        recoveryTitle: "Définir un nouveau mot de passe",
        recoveryHint: "Vous êtes revenu via votre lien de réinitialisation. Définissez maintenant votre nouveau mot de passe.",
        recoveryNewPassword: "Nouveau mot de passe",
        recoveryConfirmPassword: "Confirmer le mot de passe",
        recoverySave: "Enregistrer le mot de passe",
        recoverySaving: "Enregistrement …",
        recoverySuccess: "Votre mot de passe a été modifié avec succès.",
        recoveryContinue: "Continuer",
        recoveryTooShort: "Le mot de passe doit contenir au moins 6 caractères.",
        recoveryMismatch: "Les mots de passe ne correspondent pas.",
        forceChangeEyebrow: "Sécurité",
        forceChangeTitle: "Changez votre mot de passe maintenant",
        forceChangeHint: "Ce compte a été créé avec un mot de passe temporaire. Veuillez définir votre propre mot de passe personnel avant de continuer.",
      },
      admin: {
        titleAdmin: "Admin",
        titleStaff: "Équipe",
        welcome: "Bienvenue",
        logout: "Déconnexion",
        footerNote: "Backend : Supabase (Postgres + RLS).",
        tabOverview: "Aperçu",
        tabInbox: "Boîte de tickets",
        tabContent: "Gestionnaire de contenu",
        tabTeam: "Gestion de l'équipe",
        tabTexts: "Textes du site",
        loading: "Chargement …",
        badgeDemo: "Données de démo",
        badgeLive: "En direct · Supabase",
        infra: {
          title: "Performance technique & système",
          status: "Statut de la base de données",
          statusOnline: "En ligne",
          statusOffline: "Inaccessible",
          statusChecking: "Vérification …",
          latency: "Latence de la base de données",
          latencySub: "Dernière mesure",
          checks: "Vérifications (cette session)",
          checksSub: "réussies / échouées",
          lastChecked: "Dernière vérification",
          lastCheckedNever: "Pas encore vérifié",
          latencyChart: "Historique de latence (cette session)",
          checksChart: "Vérifications par résultat (cette session)",
          sessionNote: "Mesuré en direct via une vraie requête Supabase (profiles, HEAD/count) — aucune surveillance côté serveur, donc valable uniquement pour cette session de navigateur.",
          sessionScope: "Surveillance de session",
        },
        traffic: {
          title: "Trafic & engagement des utilisateurs",
          realtime: "Utilisateurs en temps réel",
          realtimeSub: "Actifs en ce moment",
          sessionsToday: "Sessions aujourd'hui",
          engagement: "Taux d'engagement",
          avgDuration: "Durée moyenne de session",
          sessionsChart: "Sessions (7 jours)",
          sourcesChart: "Sources de trafic",
        },
        geo: {
          title: "Géographie & démographie",
          mapTitle: "Points chauds du trafic (carte du monde)",
          mapCaption: "Taille du cercle ≈ part de trafic relative par ville (données de démo)",
          topCountries: "Principaux pays",
          languages: "Répartition des langues",
          timezones: "Fuseaux horaires actifs",
        },
        users: {
          title: "Gestion des utilisateurs & communauté",
          total: "Utilisateurs inscrits",
          newToday: "Nouveaux aujourd'hui",
          newWeekSuffix: "cette semaine",
          customers: "Clients",
          team: "Équipe (staff/admin)",
          adminSuffix: "admin(s)",
          churnNote: "Le taux de désabonnement / comptes inactifs nécessite des horodatages de connexion, non suivis actuellement — ajoutable à l'étape suivante.",
        },
        business: {
          title: "Business & conversions",
          revenue: "Chiffre d'affaires (demandé)",
          revenueSub: "demandes de réservation",
          conversion: "Taux de conversion",
          conversionSub: "Demandes / visiteurs uniques (mois, trafic démo)",
          aov: "Panier moyen (AOV)",
          bookingsTotal: "Total des demandes de réservation",
          ranking: "Classement des meilleures ventes",
          rankingEmpty: "Aucune demande de réservation pour l'instant.",
        },
        charts: {
          title: "Calculateur d'horoscopes",
          total: "Total des charts",
          today: "Calculés aujourd'hui",
          week: "Cette semaine",
          topRashi: "Rashi le plus fréquent",
        },
        activity: {
          title: "Activité récente",
          empty: "Aucune activité pour l'instant.",
        },
        security: {
          title: "Administration & sécurité",
          openTickets: "Tickets de support ouverts",
          totalSuffix: "au total",
          securityEvents: "Événements de sécurité (7 jours)",
          securityEventsSub: "Données de démo",
          log: "Journal de sécurité",
          logNote: "Les échecs de connexion réels nécessitent l'API admin de Supabase Auth — doit s'exécuter côté serveur, jamais dans le navigateur.",
        },
        inbox: {
          empty: "Aucune demande pour l'instant.",
          selectPrompt: "Sélectionnez une conversation à gauche.",
          markAnswered: "Marquer comme répondu",
          markOpen: "Marquer comme ouvert",
          replyPlaceholder: "Écrire une réponse …",
          replyButton: "Répondre",
        },
        content: {
          save: "Enregistrer",
          saving: "Enregistrement …",
          savedAt: "Dernière sauvegarde à",
        },
        team: {
          title: "Équipe & admins",
          inviteTitle: "Inviter un membre de l'équipe",
          inviteHint: "Pré-autorise un e-mail pour un rôle. Cette personne s'inscrit sur /login avec exactement cet e-mail et reçoit automatiquement ce rôle.",
          emailLabel: "E-mail",
          roleLabel: "Rôle",
          roleStaff: "Membre de l'équipe",
          roleAdmin: "Admin",
          inviteButton: "Inviter",
          noStaff: "Aucun membre de l'équipe inscrit pour l'instant.",
        },
        media: {
          tabLabel: "Médias & images des pages",
          title: "Médias & images des pages",
          hint: "Remplacez les images de remplacement sur les pages publiques. Les changements apparaissent en direct dès le téléchargement.",
          currentLabel: "Image actuelle",
          defaultBadge: "Espace réservé",
          customBadge: "Image personnalisée",
          altLabel: "Texte alternatif (accessibilité)",
          uploadButton: "Télécharger l'image",
          uploading: "Téléchargement …",
          uploadedAt: "Dernière modification",
          error: "Échec du téléchargement.",
          resetButton: "Réinitialiser",
          resetting: "Réinitialisation …",
          resetConfirm: "Vraiment réinitialiser cette image à l'espace réservé par défaut ?",
          filterAll: "Tous",
          filterOffers: "Offres & forfaits",
          filterPages: "Pages",
          filterGlobal: "Global",
          dropHint: "Glissez un fichier ici ou cliquez pour parcourir",
        },
      },
    },
  },

  es: {
    meta: {
      title: "Astrología Védica Sideral – Descodifica el Plan Real de tu Alma",
      description:
        "Astrología védica sideral con precisión matemática. Descubre tu verdadero Lagna, tus 27 Nakshatras y tu Dasha Vimshottari actual — calculado gratis.",
    },
    nav: { why: "Los 3 Pilares", compare: "Tropical vs. Sideral", faq: "Preguntas", cta: "Calcular carta" },
    hero: {
      eyebrow: "Sideral · Jyotish · Matemáticamente Exacto",
      headline: "El 86% de las personas leen el signo del zodiaco equivocado.",
      headlineAccent: "¿Cuál es realmente el tuyo?",
      subtitle:
        "La astrología occidental (tropical) se ha desviado unos 24° de las constelaciones reales debido a la precesión del eje terrestre. El sistema sideral Jyotish corrige este desfase y calcula las posiciones planetarias reales en el cielo — tu carta natal verdadera, con precisión matemática.",
    },
    form: {
      badge: "Carta Sideral Gratuita",
      title: "Calcula el plan real de tu alma",
      dob: "Fecha de nacimiento (DD.MM.AAAA)",
      dobPh: "15.08.1990",
      time: "Hora exacta (HH:MM)",
      timePh: "14:30",
      city: "Lugar de nacimiento (Ciudad)",
      cityPh: "Madrid",
      country: "País de nacimiento",
      cta: "Calcular el Plan Real (Gratis)",
      trust: "100% gratis · Sin tarjeta · Resultado instantáneo",
    },
    modal: {
      badge: "Tu momento revelador ✦",
      title: "Tu verdadera carta sideral",
      lagna: "Lagna (Ascendente)",
      nakshatra: "Nakshatra (Mansión lunar)",
      rashi: "Rashi (Signo lunar)",
      lockedTitle: "Tu Dasha actual está calculado",
      lockedBody:
        "La cronología completa de tus fases de vida — carrera, amor, prosperidad y crecimiento espiritual — te espera en el Informe Premium detallado.",
      unlock: "Descubrir VIP Cosmic Coaching (489€ / 3 meses)",
      app: "App Coming Soon — únete a la lista de espera",
      again: "Nuevo cálculo",
    },
    compare: {
      eyebrow: "Tropical vs. Sideral",
      title: "Por qué tu signo occidental probablemente es incorrecto.",
      paragraphs: [
        "El eje de la Tierra se tambalea lentamente por el espacio como una peonza — un fenómeno llamado precesión de los equinoccios. Un ciclo completo dura unos 25.800 años. Hace aproximadamente 2.000 años, cuando la astrología occidental fijó sus signos, el punto vernal y la constelación de Aries coincidían casi exactamente. Desde entonces se han alejado casi 24°.",
        "La astrología tropical (occidental) ignora deliberadamente este desfase: fija los signos a las estaciones y a la posición del Sol en el equinoccio — no a las estrellas reales. El resultado es contundente: quien se cree Aries, astronómicamente suele tener el Sol en Piscis. Casi nueve de cada diez personas viven bajo un signo que ya no coincide con el cielo real.",
        "La astrología védica sideral (Jyotish), en cambio, calcula según el campo estelar real y observable. Mediante el Ayanamsa de Lahiri — un valor de corrección preciso — las posiciones planetarias se devuelven a las verdaderas constelaciones. Así se obtiene una carta que concuerda con la astronomía y sirve de base fiable para leer el carácter, el momento y las fases de la vida.",
      ],
      tropicalLabel: "Tropical (Occidental)",
      tropicalPoints: [
        "Ligada a las estaciones, no a las estrellas",
        "Ignora el desfase de precesión de 24°",
        "12 signos solares amplios",
        "Sin sistema de timing preciso",
      ],
      siderealLabel: "Sideral (Jyotish)",
      siderealPoints: [
        "Ligada al campo estelar real",
        "El Ayanamsa de Lahiri corrige la precesión",
        "27 Nakshatras + 12 Rashis para profundidad",
        "Dasha Vimshottari para timing exacto",
      ],
    },
    why: {
      eyebrow: "Los 3 Pilares del Jyotish",
      title: "Tres coordenadas. Un plan de alma completo.",
      cards: [
        {
          title: "Lagna",
          subtitle: "El verdadero signo ascendente",
          body: "El Lagna es el grado exacto del zodiaco que ascendía por el horizonte este en el momento de tu nacimiento. Cambia aproximadamente cada dos horas y define toda la estructura de casas de tu carta — tu apariencia, tu cuerpo y el camino que tu alma ha tomado en esta vida. Sin hora de nacimiento exacta, no hay Lagna exacto.",
        },
        {
          title: "Nakshatras",
          subtitle: "27 mansiones lunares",
          body: "Donde la astrología occidental solo conoce 12 signos amplios, el Jyotish divide el cielo en 27 Nakshatras — finas mansiones lunares, cada una con su deidad, simbolismo y psicología. Tu Nakshatra de nacimiento describe tu naturaleza más profunda, tus instintos y tus patrones emocionales con una precisión que 12 signos jamás alcanzarán.",
        },
        {
          title: "Dasha Vimshottari",
          subtitle: "La cronología de tu vida",
          body: "El Dasha es el corazón del Jyotish: un ciclo de 120 años de periodos planetarios que revela cuándo se activan ciertas fuerzas en tu vida. Responde no solo al qué, sino al cuándo — cuándo maduran la carrera, el amor y la prosperidad. Un timing que ningún método occidental ofrece.",
        },
      ],
    },
    faq: {
      eyebrow: "Preguntas frecuentes",
      title: "Lo que preguntan los buscadores curiosos.",
      items: [
        { q: "¿Por qué mi signo es diferente al habitual?", a: "Porque el Jyotish usa el sistema sideral, que calcula según las posiciones estelares reales. La astrología occidental está desviada unos 24° por la precesión, colocando a la mayoría un signo demasiado alto en el sistema tropical. Tu signo sideral es astronómicamente correcto." },
        { q: "¿Realmente necesito mi hora exacta de nacimiento?", a: "Para el Lagna (ascendente) y la distribución de casas: sí. El Lagna cambia aproximadamente cada dos horas, así que incluso unos minutos de diferencia alteran las casas. El Rashi (signo lunar) y tu Nakshatra son significativos con una hora aproximada, pero para una carta completa recomendamos la hora exacta de tu partida de nacimiento." },
        { q: "¿Qué sistema de casas utilizan?", a: "Usamos el sistema clásico de signos completos (Whole Sign) común en el norte de la India, donde cada Rashi forma una casa completa. Es el sistema védico original y produce asignaciones de casas claras y sin distorsión — especialmente potente combinado con los periodos Dasha." },
        { q: "¿Cómo leo correctamente mi resultado?", a: "Empieza por tu Lagna — es el escenario de tu vida. Luego observa tu Rashi y tu Nakshatra para tu naturaleza emocional y psicológica. Finalmente, tu Dasha Vimshottari en curso muestra qué capítulo estás viviendo ahora. El Informe Premium te guía paso a paso por estos niveles." },
        { q: "¿En qué se diferencia de la astrología occidental?", a: "La astrología occidental es tropical (ligada a las estaciones), usa 12 signos y no tiene timing sistemático. El Jyotish es sideral (ligado a las estrellas reales), usa 27 Nakshatras para la profundidad y el Dasha Vimshottari para un timing preciso. El resultado es mucho más personal, concreto y fundamentado en la astronomía." },
      ],
    },
    contact: {
      eyebrow: "Contacto",
      title: "¿Preguntas para nuestros astrólogos védicos?",
      subtitle: "Escríbenos — respondemos de forma personal y discreta, normalmente en 24 horas.",
      name: "Nombre",
      namePh: "Tu nombre",
      email: "Correo electrónico",
      emailPh: "nombre@ejemplo.es",
      message: "Mensaje / Consulta",
      messagePh: "Tu pregunta sobre tu carta, un Dasha o el Informe Premium …",
      cta: "Enviar mensaje",
      success: "¡Gracias! Hemos recibido tu mensaje — te responderemos pronto.",
    },
    app: {
      login: {
        signInEyebrow: "Bienvenido de nuevo",
        signInTitle: "Iniciar sesión",
        signUpEyebrow: "Crear una cuenta",
        signUpTitle: "Registrarse",
        subtitle: "Accede a tu portal de cliente o al área de equipo.",
        nameLabel: "Nombre",
        emailLabel: "Correo electrónico",
        passwordLabel: "Contraseña",
        submitLogin: "Iniciar sesión",
        submitRegister: "Registrarse",
        submitting: "Un momento …",
        registerSuccess: "¡Cuenta creada! Si la confirmación por correo está activada, revisa tu bandeja de entrada — si no, ya has iniciado sesión.",
        noAccount: "¿Aún no tienes cuenta?",
        switchToRegister: "Registrarse",
        haveAccount: "¿Ya tienes una cuenta?",
        switchToLogin: "Iniciar sesión",
        backHome: "← Volver al inicio",
        forgotPassword: "¿Olvidaste tu contraseña?",
        resetTitle: "Restablecer contraseña",
        resetHint: "Introduce tu correo electrónico — te enviaremos un enlace para restablecerla.",
        resetSend: "Enviar enlace",
        resetSending: "Enviando …",
        resetSuccess: "Si existe una cuenta con este correo, se ha enviado un enlace de restablecimiento. Revisa tu bandeja de entrada.",
        resetCancel: "Cancelar",
        recoveryEyebrow: "Recuperación de contraseña",
        recoveryTitle: "Establecer nueva contraseña",
        recoveryHint: "Has vuelto a través de tu enlace de restablecimiento. Establece ahora tu nueva contraseña.",
        recoveryNewPassword: "Nueva contraseña",
        recoveryConfirmPassword: "Confirmar contraseña",
        recoverySave: "Guardar contraseña",
        recoverySaving: "Guardando …",
        recoverySuccess: "Tu contraseña se ha cambiado correctamente.",
        recoveryContinue: "Continuar",
        recoveryTooShort: "La contraseña debe tener al menos 6 caracteres.",
        recoveryMismatch: "Las contraseñas no coinciden.",
        forceChangeEyebrow: "Seguridad",
        forceChangeTitle: "Cambia tu contraseña ahora",
        forceChangeHint: "Esta cuenta se creó con una contraseña temporal. Establece ahora tu propia contraseña personal antes de continuar.",
      },
      admin: {
        titleAdmin: "Admin",
        titleStaff: "Equipo",
        welcome: "Bienvenido",
        logout: "Cerrar sesión",
        footerNote: "Backend: Supabase (Postgres + RLS).",
        tabOverview: "Resumen",
        tabInbox: "Bandeja de tickets",
        tabContent: "Gestor de contenido",
        tabTeam: "Gestión de equipo",
        tabTexts: "Textos del sitio",
        loading: "Cargando …",
        badgeDemo: "Datos de demostración",
        badgeLive: "En vivo · Supabase",
        infra: {
          title: "Rendimiento técnico y sistema",
          status: "Estado de la base de datos",
          statusOnline: "En línea",
          statusOffline: "No disponible",
          statusChecking: "Comprobando …",
          latency: "Latencia de la base de datos",
          latencySub: "Última medición",
          checks: "Comprobaciones (esta sesión)",
          checksSub: "exitosas / fallidas",
          lastChecked: "Última comprobación",
          lastCheckedNever: "Aún no comprobado",
          latencyChart: "Historial de latencia (esta sesión)",
          checksChart: "Comprobaciones por resultado (esta sesión)",
          sessionNote: "Medido en vivo mediante una consulta real a Supabase (profiles, HEAD/count) — sin monitoreo del lado del servidor, válido solo para esta sesión del navegador.",
          sessionScope: "Monitoreo de sesión",
        },
        traffic: {
          title: "Tráfico y participación de usuarios",
          realtime: "Usuarios en tiempo real",
          realtimeSub: "Activos ahora mismo",
          sessionsToday: "Sesiones hoy",
          engagement: "Tasa de participación",
          avgDuration: "Duración media de sesión",
          sessionsChart: "Sesiones (7 días)",
          sourcesChart: "Fuentes de tráfico",
        },
        geo: {
          title: "Geografía y demografía",
          mapTitle: "Puntos calientes de tráfico (mapa mundial)",
          mapCaption: "Tamaño del círculo ≈ proporción relativa de tráfico por ciudad (datos de demostración)",
          topCountries: "Principales países",
          languages: "Distribución de idiomas",
          timezones: "Zonas horarias activas",
        },
        users: {
          title: "Gestión de usuarios y comunidad",
          total: "Usuarios registrados",
          newToday: "Nuevos hoy",
          newWeekSuffix: "esta semana",
          customers: "Clientes",
          team: "Equipo (staff/admin)",
          adminSuffix: "admin(s)",
          churnNote: "La tasa de abandono / cuentas inactivas requiere marcas de tiempo de inicio de sesión, no registradas actualmente — se puede añadir como siguiente paso.",
        },
        business: {
          title: "Negocio y conversiones",
          revenue: "Ingresos (solicitados)",
          revenueSub: "solicitudes de reserva",
          conversion: "Tasa de conversión",
          conversionSub: "Solicitudes / visitantes únicos (mes, tráfico demo)",
          aov: "Valor medio de pedido (AOV)",
          bookingsTotal: "Total de solicitudes de reserva",
          ranking: "Ranking de más vendidos",
          rankingEmpty: "Aún no hay solicitudes de reserva de ofertas.",
        },
        charts: {
          title: "Calculadora de horóscopos",
          total: "Total de charts",
          today: "Calculados hoy",
          week: "Esta semana",
          topRashi: "Rashi más frecuente",
        },
        activity: {
          title: "Actividad reciente",
          empty: "Aún no hay actividad.",
        },
        security: {
          title: "Administración y seguridad",
          openTickets: "Tickets de soporte abiertos",
          totalSuffix: "en total",
          securityEvents: "Eventos de seguridad (7 días)",
          securityEventsSub: "Datos de demostración",
          log: "Registro de seguridad",
          logNote: "Los inicios de sesión fallidos reales requieren la API de administración de Supabase Auth — debe ejecutarse en el servidor, nunca en el navegador.",
        },
        inbox: {
          empty: "Aún no hay solicitudes.",
          selectPrompt: "Selecciona una conversación a la izquierda.",
          markAnswered: "Marcar como respondido",
          markOpen: "Marcar como abierto",
          replyPlaceholder: "Escribe una respuesta …",
          replyButton: "Responder",
        },
        content: {
          save: "Guardar",
          saving: "Guardando …",
          savedAt: "Última vez guardado a las",
        },
        team: {
          title: "Equipo y administradores",
          inviteTitle: "Invitar a un miembro del equipo",
          inviteHint: "Autoriza previamente un correo para un rol. Esa persona se registra en /login con exactamente ese correo y recibe automáticamente ese rol.",
          emailLabel: "Correo electrónico",
          roleLabel: "Rol",
          roleStaff: "Staff",
          roleAdmin: "Admin",
          inviteButton: "Invitar",
          noStaff: "Aún no hay ningún miembro del equipo registrado.",
        },
        media: {
          tabLabel: "Medios e imágenes de página",
          title: "Medios e imágenes de página",
          hint: "Reemplaza las imágenes de marcador de posición en las páginas públicas. Los cambios aparecen en vivo en cuanto se suben.",
          currentLabel: "Imagen actual",
          defaultBadge: "Marcador de posición",
          customBadge: "Imagen personalizada",
          altLabel: "Texto alternativo (accesibilidad)",
          uploadButton: "Subir imagen",
          uploading: "Subiendo …",
          uploadedAt: "Última modificación",
          error: "Error al subir.",
          resetButton: "Restablecer",
          resetting: "Restableciendo …",
          resetConfirm: "¿Seguro que quieres restablecer esta imagen al marcador de posición predeterminado?",
          filterAll: "Todas",
          filterOffers: "Ofertas y paquetes",
          filterPages: "Páginas",
          filterGlobal: "Global",
          dropHint: "Arrastra un archivo aquí o haz clic para explorar",
        },
      },
    },
  },
};
